/**
 * Artisan Theme — Main JavaScript
 * Vanilla JS, no dependencies required
 * Covers: Cart Drawer, Mobile Nav, Search, Product Gallery,
 *         Variant Selection, Quantity, Sticky Header, Accordion,
 *         Predictive Search, Toast, Back to Top, AOS
 */

(function () {
  'use strict';

  /* =====================================================
     UTILITIES
  ===================================================== */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function off(el, ev, fn) { if (el) el.removeEventListener(ev, fn); }
  function debounce(fn, ms) {
    let t; return function () { clearTimeout(t); t = setTimeout(() => fn.apply(this, arguments), ms); };
  }
  function formatMoney(cents) {
    const fmt = (window.theme && window.theme.moneyFormat) || '₹{{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt.replace(/\{\{\s*amount\s*\}\}/g, amount)
              .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.floor(cents / 100))
              .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
              .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.floor(cents / 100).toString().replace('.', ','));
  }
  function trap(el) {
    const focusable = el.querySelectorAll('a[href],button:not([disabled]),input,textarea,select,[tabindex]:not([tabindex="-1"])');
    const first = focusable[0], last = focusable[focusable.length - 1];
    el.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault(); (e.shiftKey ? last : first).focus();
      }
    });
  }

  /* =====================================================
     TOAST NOTIFICATIONS
  ===================================================== */
  function showToast(msg, type, duration) {
    const container = $('#toast-container');
    if (!container) return;
    const el = document.createElement('div');
    el.className = 'toast toast--' + (type || 'info');
    el.innerHTML = msg;
    container.appendChild(el);
    setTimeout(function () {
      el.style.opacity = '0';
      el.style.transform = 'translateY(10px)';
      el.style.transition = '0.3s';
      setTimeout(() => el.remove(), 350);
    }, duration || 3000);
  }

  /* =====================================================
     SITE OVERLAY
  ===================================================== */
  const overlay = $('#site-overlay');
  function showOverlay() { overlay && overlay.classList.add('is-active'); }
  function hideOverlay() { overlay && overlay.classList.remove('is-active'); }
  on(overlay, 'click', function () { closeAll(); });

  function closeAll() {
    closeCartDrawer();
    closeMobileNav();
    closeSearch();
  }

  /* =====================================================
     ANNOUNCEMENT BAR
  ===================================================== */
  on($('.announcement-bar__close'), 'click', function () {
    var bar = $('.announcement-bar');
    if (bar) {
      // Avoid forced reflow - use CSS class instead of offsetHeight
      bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
      bar.style.maxHeight = '0';
      bar.style.opacity = '0';
      bar.style.overflow = 'hidden';
      bar.style.paddingTop = '0';
      bar.style.paddingBottom = '0';
      setTimeout(function() { bar.classList.add('is-hidden'); }, 350);
      sessionStorage.setItem('announcementClosed', '1');
    }
  });
  if (sessionStorage.getItem('announcementClosed')) {
    var bar = $('.announcement-bar');
    if (bar) bar.classList.add('is-hidden');
  }

  /* =====================================================
     STICKY HEADER
  ===================================================== */
  const header = $('.site-header');
  let lastScroll = 0;
  if (header) {
    window.addEventListener('scroll', debounce(function () {
      const scroll = window.scrollY;
      header.classList.toggle('is-sticky', scroll > 10);
      if (scroll > 300 && scroll > lastScroll) {
        header.classList.add('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastScroll = scroll;
    }, 50), { passive: true });
  }

  /* =====================================================
     MOBILE NAVIGATION
  ===================================================== */
  const mobileNav = $('.mobile-nav');
  function openMobileNav() {
    mobileNav && mobileNav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    showOverlay();
  }
  function closeMobileNav() {
    mobileNav && mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
    hideOverlay();
  }
  on($('.header__menu-btn'), 'click', openMobileNav);
  on($('.mobile-nav__close'), 'click', closeMobileNav);

  // Submenu toggles
  $$('.mobile-nav__toggle').forEach(function (btn) {
    on(btn, 'click', function () {
      const sub = this.closest('.mobile-nav__item').querySelector('.mobile-nav__sub');
      const icon = this.querySelector('svg');
      if (sub) {
        sub.classList.toggle('is-open');
        if (icon) icon.style.transform = sub.classList.contains('is-open') ? 'rotate(180deg)' : '';
      }
    });
  });

  /* =====================================================
     SEARCH OVERLAY
  ===================================================== */
  const searchOverlay = $('.search-overlay');
  const searchInput = searchOverlay && searchOverlay.querySelector('.search-overlay__input');

  function openSearch() {
    searchOverlay && searchOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    showOverlay();
    setTimeout(() => searchInput && searchInput.focus(), 100);
  }
  function closeSearch() {
    searchOverlay && searchOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    hideOverlay();
  }
  $$('.header__search-btn').forEach(btn => on(btn, 'click', openSearch));
  on($('.search-overlay__close'), 'click', closeSearch);
  on(document, 'keydown', function (e) { if (e.key === 'Escape') closeAll(); });

  /* Predictive Search */
  if (searchInput && window.theme && window.theme.settings && window.theme.settings.predictiveSearch) {
    const resultsBox = $('.predictive-results');
    const debouncedSearch = debounce(function () {
      const q = searchInput.value.trim();
      if (q.length < 2) { if (resultsBox) resultsBox.innerHTML = ''; return; }
      const limit = (window.theme.settings.predictiveSearchLimit) || 6;
      fetch(window.theme.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=' + limit + '&section_id=predictive-search')
        .then(r => r.text())
        .then(function (html) {
          if (!resultsBox) return;
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const results = doc.querySelector('#shopify-section-predictive-search');
          resultsBox.innerHTML = results ? results.innerHTML : '';
        })
        .catch(() => {});
    }, 300);
    on(searchInput, 'input', debouncedSearch);
  }

  /* =====================================================
     CART DRAWER
  ===================================================== */
  const cartDrawer = $('.cart-drawer');

  function openCartDrawer() {
    cartDrawer && cartDrawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    showOverlay();
    if (cartDrawer) trap(cartDrawer);
  }
  function closeCartDrawer() {
    cartDrawer && cartDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
    hideOverlay();
  }
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

  on($('.cart-drawer__close'), 'click', closeCartDrawer);
  $$('.header__cart-btn').forEach(btn => on(btn, 'click', openCartDrawer));

  /* Cart AJAX helpers */
  function cartFetch(url, body) {
    const endpoint = /\.js($|\?)/.test(url) ? url : (url + '.js');
    return fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(async function (r) {
      const raw = await r.text();
      if (!raw) return {};
      try {
        return JSON.parse(raw);
      } catch (e) {
        // Some storefront/app setups can return HTML even on success.
        if (r.ok) return { ok: true };
        throw new Error('Cart request failed');
      }
    });
  }

  function getCart() {
    return fetch(window.theme.routes.cart + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json());
  }

  function updateCartUI(cart) {
    // Update item count badges
    const counts = $$('.header__cart-count, .etsy-cart-count');
    counts.forEach(function (el) {
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
    });

    // Update shipping bar
    updateShippingBar(cart.total_price);

    // Re-render cart items
    renderCartItems(cart);

    // Update subtotal
    const subtotalEl = $('.cart-drawer__subtotal-price');
    if (subtotalEl) subtotalEl.innerHTML = formatMoney(cart.total_price);

    // Cart page subtotal
    const pageSubtotal = $('#cart-subtotal');
    if (pageSubtotal) pageSubtotal.innerHTML = formatMoney(cart.total_price);
  }

  function updateShippingBar(totalPrice) {
    const threshold = parseInt((window.theme && window.theme.settings && window.theme.settings.freeShippingThreshold) || '49900');
    const fill = $('.shipping-bar__fill');
    const text = $('.shipping-bar__text');
    if (!fill) return;
    const pct = Math.min((totalPrice / threshold) * 100, 100);
    fill.style.width = pct + '%';
    if (text) {
      if (totalPrice >= threshold) {
        text.innerHTML = '<strong>🎉 ' + window.theme.strings.freeShipping + '</strong>';
      } else {
        const remaining = threshold - totalPrice;
        text.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more for free shipping!';
      }
    }
  }

  function renderCartItems(cart) {
    const container = $('.cart-drawer__items');
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = '<div class="cart-drawer__empty"><svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg><p class="cart-drawer__empty-title">' + window.theme.strings.cartEmpty + '</p><a href="/collections/all" class="btn btn--primary btn--sm" onclick="closeCartDrawer()">Shop Now</a></div>';
      return;
    }

    let html = '';
    cart.items.forEach(function (item) {
      const imgSrc = item.image ? item.image.replace('.jpg', '_200x200.jpg').replace('.png', '_200x200.png').replace('.webp', '_200x200.webp') : '';
      const comparePrice = item.original_price > item.price ? '<s>' + formatMoney(item.original_price) + '</s> ' : '';
      html += '<div class="cart-item" data-key="' + item.key + '">' +
        '<a href="' + item.url + '" class="cart-item__img">' + (imgSrc ? '<img src="' + imgSrc + '" alt="' + item.product_title + '" loading="lazy">' : '') + '</a>' +
        '<div class="cart-item__info">' +
          '<div class="cart-item__vendor">' + (item.vendor || '') + '</div>' +
          '<a href="' + item.url + '" class="cart-item__title">' + item.product_title + '</a>' +
          (item.variant_title && item.variant_title !== 'Default Title' ? '<div class="cart-item__variant">' + item.variant_title + '</div>' : '') +
          '<div class="cart-item__bottom">' +
            '<div class="cart-qty">' +
              '<button class="cart-qty__btn cart-qty__minus" data-key="' + item.key + '" data-qty="' + (item.quantity - 1) + '" aria-label="Decrease">−</button>' +
              '<span class="cart-qty__num">' + item.quantity + '</span>' +
              '<button class="cart-qty__btn cart-qty__plus" data-key="' + item.key + '" data-qty="' + (item.quantity + 1) + '" aria-label="Increase">+</button>' +
            '</div>' +
            '<div class="cart-item__price">' + comparePrice + formatMoney(item.line_price) + '</div>' +
          '</div>' +
          '<button class="cart-item__remove" data-key="' + item.key + '">' + (window.theme.strings.remove || 'Remove') + '</button>' +
        '</div>' +
        '</div>';
    });

    container.innerHTML = html;
    bindCartItemEvents();
  }

  function bindCartItemEvents() {
    $$('.cart-qty__btn').forEach(function (btn) {
      on(btn, 'click', function () {
        const key = this.dataset.key;
        const qty = parseInt(this.dataset.qty);
        if (qty < 0) return;
        this.disabled = true;
        cartFetch(window.theme.routes.cartChange, { id: key, quantity: qty })
          .then(updateCartUI)
          .catch(() => window.location.reload());
      });
    });

    $$('.cart-item__remove').forEach(function (btn) {
      on(btn, 'click', function () {
        const key = this.dataset.key;
        const item = this.closest('.cart-item');
        item.style.opacity = '0.4';
        cartFetch(window.theme.routes.cartChange, { id: key, quantity: 0 })
          .then(updateCartUI)
          .catch(() => window.location.reload());
      });
    });
  }

  // Initialize cart item events if drawer has items
  bindCartItemEvents();

  /* =====================================================
     ADD TO CART
  ===================================================== */
  $$('[data-add-to-cart]').forEach(function (form) {
    on(form, 'submit', function (e) {
      e.preventDefault();
      if (form.dataset.atcSubmitting === 'true') return;
      form.dataset.atcSubmitting = 'true';
      const btn = form.querySelector('[data-atc-btn]') || form.querySelector('[type=submit]');
      const variantId = form.querySelector('[name=id]').value;
      const qty = parseInt((form.querySelector('[name=quantity]') || {}).value || 1);

      if (btn) { btn.disabled = true; btn.textContent = window.theme.strings.addingToCart || 'Adding...'; }

      cartFetch(window.theme.routes.cartAdd, { id: variantId, quantity: qty })
        .then(function (item) {
          if (item && item.status && Number(item.status) >= 400) {
            throw new Error(item.description || item.message || 'Error');
          }
          return getCart().then(function (cart) {
            updateCartUI(cart);
            showToast('✓ Added to cart', 'success');
          }).catch(function () {
            // If cart refresh fails but add succeeds, avoid false-negative error toast.
            showToast('✓ Added to cart', 'success');
          });
        })
        .catch(function (err) {
          showToast((err && err.message) ? err.message : 'Could not add item. Please try again.', 'error');
        })
        .finally(function () {
          if (btn) { btn.disabled = false; btn.textContent = window.theme.strings.addToCart || 'Add to Cart'; }
          form.dataset.atcSubmitting = 'false';
        });
    });
  });

  /* =====================================================
     QUICK ADD (product cards)
  ===================================================== */
  $$('[data-quick-add]').forEach(function (btn) {
    on(btn, 'click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      const variantId = this.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;
      cartFetch(window.theme.routes.cartAdd, { id: variantId, quantity: 1 })
        .then(function () {
          getCart().then(function (cart) {
            updateCartUI(cart);
            showToast('✓ Added to cart', 'success');
            btn.disabled = false;
          });
        })
        .catch(function () {
          showToast('Could not add item.', 'error');
          btn.disabled = false;
        });
    });
  });

  /* =====================================================
     PRODUCT GALLERY
  ===================================================== */
  const galleryMain = $('.product-gallery__main');
  const galleryThumbs = $$('.product-gallery__thumb');

  galleryThumbs.forEach(function (thumb, i) {
    on(thumb, 'click', function () {
      const src = this.dataset.src;
      const mainImg = galleryMain && galleryMain.querySelector('img');
      if (mainImg && src) {
        mainImg.style.opacity = '0.5';
        setTimeout(function () {
          mainImg.src = src;
          mainImg.style.opacity = '1';
        }, 150);
      }
      galleryThumbs.forEach(t => t.classList.remove('is-active'));
      this.classList.add('is-active');
    });
  });

  /* =====================================================
     VARIANT SELECTOR
  ===================================================== */
  const productPage = $('[data-product-page]');
  if (productPage) {
    const variantForm = productPage.querySelector('[data-add-to-cart]');
    const variantIdInput = variantForm && variantForm.querySelector('[name=id]');
    const priceEl = productPage.querySelector('[data-price]');
    const comparePriceEl = productPage.querySelector('[data-compare-price]');
    const atcBtn = variantForm && variantForm.querySelector('[data-atc-btn]');
    const buyNowBtn = productPage.querySelector('#buy-now-btn');
    const stickyAtcBtn = $('.sticky-atc__btn');
    const galleryMainImg = productPage.querySelector('#gallery-main-img');

    let productData;
    try { productData = JSON.parse(($('[data-product-json]') || {}).textContent || 'null'); } catch(e) {}

    function getSelectedVariant() {
      if (!productData) return null;
      const selected = {};
      $$('select[data-option-name]', productPage).forEach(function (sel) {
        selected[sel.dataset.optionName] = sel.value;
      });
      return productData.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return opt === selected['option' + (i + 1)];
        });
      });
    }

    function updateVariantUI(variant) {
      if (!variant) return;
      if (variantIdInput) variantIdInput.value = variant.id;

      if (priceEl) priceEl.innerHTML = formatMoney(variant.price);
      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.innerHTML = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      var unavailable = !variant.available;

      if (atcBtn) {
        atcBtn.disabled = unavailable;
        atcBtn.textContent = unavailable ? (window.theme.strings.soldOut || 'Sold Out') : (window.theme.strings.addToCart || 'Add to Cart');
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = unavailable;
        buyNowBtn.textContent = unavailable ? 'Coming Soon' : (window.theme.strings.buyNow || 'Buy Now');
        buyNowBtn.style.opacity = unavailable ? '0.5' : '1';
      }
      if (stickyAtcBtn) {
        stickyAtcBtn.disabled = unavailable;
        stickyAtcBtn.textContent = unavailable ? (window.theme.strings.soldOut || 'Sold Out') : (window.theme.strings.addToCart || 'Add to Cart');
      }

      // Update gallery image if variant has an image
      if (galleryMainImg && variant.featured_image && variant.featured_image.src) {
        galleryMainImg.src = variant.featured_image.src;
        var thumbs = $$('.product-gallery__thumb', productPage);
        thumbs.forEach(function(t) { t.classList.remove('is-active'); });
        thumbs.forEach(function(t) {
          if (t.dataset.src && variant.featured_image.src.indexOf(t.dataset.src.split('?')[0].replace(/_\d+x\d*/, '')) > -1) {
            t.classList.add('is-active');
          }
        });
      }

      // Update selected label text
      var optionLabels = $$('.option-group__selected', productPage);
      variant.options.forEach(function(opt, i) {
        if (optionLabels[i]) optionLabels[i].textContent = opt;
      });

      // Update URL
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    // Option buttons click handler
    $$('[data-option-btn]', productPage).forEach(function (btn) {
      on(btn, 'click', function () {
        var name = this.dataset.optionName;
        var val = this.dataset.optionValue;
        // Update active state on buttons in same group
        var group = this.closest('.option-group__values');
        if (group) {
          group.querySelectorAll('[data-option-btn]').forEach(function(b) { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
        }
        this.classList.add('is-active');
        this.setAttribute('aria-pressed', 'true');
        // Update hidden select
        var sel = productPage.querySelector('select[data-option-name="' + name + '"]');
        if (sel) { sel.value = val; }
        // Find and apply variant
        var v = getSelectedVariant();
        if (v) updateVariantUI(v);
      });
    });

    // Also handle if selects are used directly
    $$('select[data-option-name]', productPage).forEach(function (sel) {
      on(sel, 'change', function () {
        var v = getSelectedVariant();
        if (v) updateVariantUI(v);
      });
    });

    // On page load, if URL has ?variant=, select it
    (function() {
      var params = new URLSearchParams(window.location.search);
      var vid = params.get('variant');
      if (vid && productData) {
        var v = productData.variants.find(function(vr) { return String(vr.id) === vid; });
        if (v) {
          v.options.forEach(function(opt, i) {
            var sel = productPage.querySelector('select[data-option-name="option' + (i+1) + '"]');
            if (sel) sel.value = opt;
            var btns = $$('[data-option-name="option' + (i+1) + '"]', productPage);
            btns.forEach(function(b) {
              if (b.tagName === 'BUTTON') {
                b.classList.toggle('is-active', b.dataset.optionValue === opt);
                b.setAttribute('aria-pressed', b.dataset.optionValue === opt ? 'true' : 'false');
              }
            });
          });
          updateVariantUI(v);
        }
      }
    })();
  }

  /* =====================================================
     QUANTITY SELECTOR
  ===================================================== */
  $$('.quantity-selector').forEach(function (qs) {
    const minus = qs.querySelector('.quantity-selector__btn--minus');
    const plus = qs.querySelector('.quantity-selector__btn--plus');
    const input = qs.querySelector('.quantity-selector__input');
    on(minus, 'click', function () { if (input && parseInt(input.value) > 1) input.value = parseInt(input.value) - 1; });
    on(plus, 'click', function () { if (input) input.value = parseInt(input.value) + 1; });
    on(input, 'change', function () { if (parseInt(this.value) < 1) this.value = 1; });
  });

  /* =====================================================
     STICKY ADD TO CART
  ===================================================== */
  const stickyAtc = $('.sticky-atc');
  const productInfoActions = $('.product-info__actions');
  if (stickyAtc && productInfoActions) {
    const obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        stickyAtc.style.display = entry.isIntersecting ? 'none' : 'block';
      });
    }, { threshold: 0.1 });
    obs.observe(productInfoActions);
  }
  on($('.sticky-atc__btn'), 'click', function () {
    const mainForm = $('[data-add-to-cart]');
    if (mainForm) mainForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });

  /* =====================================================
     DEFERRED NON-CRITICAL INIT (runs when main thread is idle)
  ===================================================== */
  var deferInit = function () {

  /* =====================================================
     ACCORDION
  ===================================================== */
  $$('.accordion__btn').forEach(function (btn) {
    on(btn, 'click', function () {
      const content = this.nextElementSibling;
      const isOpen = this.getAttribute('aria-expanded') === 'true';
      $$('.accordion__btn').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
        const c = b.nextElementSibling;
        if (c) c.classList.remove('is-open');
      });
      if (!isOpen) {
        this.setAttribute('aria-expanded', 'true');
        if (content) content.classList.add('is-open');
      }
    });
  });

  /* =====================================================
     COLLECTION FILTERS (Mobile Drawer)
  ===================================================== */
  const filterToggle = $('.collection-filter-toggle');
  const filterDrawer = $('.filter-drawer-mobile');
  const filterClose = $('.filter-drawer-mobile__close');
  on(filterToggle, 'click', function () {
    filterDrawer && filterDrawer.classList.add('is-open');
    showOverlay();
  });
  on(filterClose, 'click', function () {
    filterDrawer && filterDrawer.classList.remove('is-open');
    hideOverlay();
  });

  // Sort select — merge sort_by into current URL so filter.p.tag etc. stay
  $$('[data-sort-select]').forEach(function (el) {
    on(el, 'change', function () {
      try {
        var next = new URL(this.value, window.location.origin);
        var sortBy = next.searchParams.get('sort_by');
        var u = new URL(window.location.href);
        if (sortBy) u.searchParams.set('sort_by', sortBy);
        else u.searchParams.delete('sort_by');
        u.searchParams.delete('page');
        window.location.href = u.pathname + (u.search || '');
      } catch (e) {
        window.location.href = this.value;
      }
    });
  });

  /* =====================================================
     BACK TO TOP
  ===================================================== */
  const btt = $('#back-to-top');
  if (btt) {
    window.addEventListener('scroll', debounce(function () {
      btt.classList.toggle('is-visible', window.scrollY > 500);
    }, 100), { passive: true });
    on(btt, 'click', function () { window.scrollTo({ top: 0, behavior: 'smooth' }); });
  }

  /* =====================================================
     SCROLL REVEAL (minimal AOS replacement)
  ===================================================== */
  const aosElements = $$('[data-aos]');
  if (aosElements.length) {
    // Use rAF to avoid blocking main thread during initial render
    requestAnimationFrame(function() {
      const revealObs = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
            revealObs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
      aosElements.forEach(function(el) { revealObs.observe(el); });
    });
  }

  /* =====================================================
     HEADER SORT (moved from inline script)
  ===================================================== */
  $$('.js-header-collection-sort').forEach(function (sel) {
    on(sel, 'change', function () {
      var base = this.getAttribute('data-sort-base');
      if (base) {
        var u = new URL(base, window.location.origin);
        u.searchParams.set('sort_by', this.value);
        u.searchParams.delete('page');
        window.location.href = u.pathname + (u.search ? u.search : '');
        return;
      }
      var u = new URL(window.location.href);
      u.searchParams.set('sort_by', this.value);
      u.searchParams.delete('page');
      window.location.href = u.pathname + (u.search || '');
    });
  });

  /* =====================================================
     CATEGORIES DROPDOWN (mobile touch)
  ===================================================== */
  var catBtn = $('.etsy-categories-btn');
  var catWrap = $('.etsy-cat-wrapper');
  if (catBtn && catWrap) {
    on(catBtn, 'click', function (e) {
      e.preventDefault();
      catWrap.classList.toggle('is-open');
    });
    on(document, 'click', function (e) {
      if (!catWrap.contains(e.target)) catWrap.classList.remove('is-open');
    });
  }

  /* =====================================================
     SLIDER DRAG SCROLL (moved from inline script)
  ===================================================== */
  $$('.etsy-slider__list').forEach(function (slider) {
    var isDown = false, startX, scrollLeft;
    on(slider, 'mousedown', function (e) {
      isDown = true;
      slider.style.cursor = 'grabbing';
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });
    on(slider, 'mouseleave', function () { isDown = false; slider.style.cursor = ''; });
    on(slider, 'mouseup', function () { isDown = false; slider.style.cursor = ''; });
    on(slider, 'mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault();
      slider.scrollLeft = scrollLeft - ((e.pageX - slider.offsetLeft) - startX) * 2;
    });
  });

  /* =====================================================
     COPY COUPON CODE
  ===================================================== */
  on($('[data-copy-code]'), 'click', function () {
    const code = this.dataset.copyCode;
    navigator.clipboard && navigator.clipboard.writeText(code).then(function () {
      showToast('Coupon code copied!', 'success');
    });
  });

  /* =====================================================
     NEWSLETTER FORM (Shopify Customer)
  ===================================================== */
  const newsletterForm = $('[data-newsletter-form]');
  on(newsletterForm, 'submit', function (e) {
    const email = (this.querySelector('[type=email]') || {}).value;
    if (!email || !email.includes('@')) {
      e.preventDefault();
      showToast('Please enter a valid email.', 'error');
    }
  });

  /* =====================================================
     LAZY LOAD IMAGES (native fallback)
  ===================================================== */
  if ('loading' in HTMLImageElement.prototype === false) {
    $$('img[loading=lazy]').forEach(function (img) {
      img.src = img.dataset.src || img.src;
    });
  }

  /* =====================================================
     MEGA MENU KEYBOARD SUPPORT
  ===================================================== */
  $$('.nav__item').forEach(function (item) {
    const link = item.querySelector('.nav__link');
    const dropdown = item.querySelector('.nav__dropdown, .nav__mega');
    if (!dropdown) return;
    on(link, 'focus', function () { item.classList.add('is-focused'); });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) item.classList.remove('is-focused');
    });
  });

  /* =====================================================
     CART PAGE (separate from drawer)
  ===================================================== */
  $$('[data-cart-remove]').forEach(function (btn) {
    on(btn, 'click', function () {
      const key = this.dataset.cartRemove;
      const row = this.closest('[data-cart-item]');
      if (row) { row.style.opacity = '0.4'; row.style.pointerEvents = 'none'; }
      cartFetch(window.theme.routes.cartChange, { id: key, quantity: 0 })
        .then(function (cart) {
          if (cart.item_count === 0) window.location.reload();
          else window.location.reload();
        });
    });
  });

  $$('[data-cart-qty-btn]').forEach(function (btn) {
    on(btn, 'click', function () {
      const key = this.dataset.key;
      const qty = parseInt(this.dataset.qty);
      if (qty < 0) return;
      this.disabled = true;
      cartFetch(window.theme.routes.cartChange, { id: key, quantity: qty })
        .then(function () { window.location.reload(); });
    });
  });

  /* Expose openCartDrawer globally for section buttons */
  window.theme = window.theme || {};
  window.theme.openCart = openCartDrawer;

  /* Final: init cart count from page data attribute */
  const cartCountInit = document.body.dataset.cartCount;
  if (cartCountInit) {
    $$('.header__cart-count, .etsy-cart-count').forEach(function (el) {
      el.textContent = cartCountInit;
      el.classList.toggle('is-empty', cartCountInit === '0');
    });
  }

  }; // end deferInit
  if ('requestIdleCallback' in window) { requestIdleCallback(deferInit, { timeout: 2000 }); }
  else { setTimeout(deferInit, 50); }

})();
